import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import Badge from 'components/Badge';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const BadgesDocs = () => {
  return (
    <div>
      <PageHeader>
        <Title>Badges</Title>
        <Subtitle>
          Unified badge component for labels, tags, status indicators, and counts. Supports
          multiple variants, shapes, sizes, and optional count badges.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Component Location</SectionTitle>
        <SectionDescription>
          The unified Badge component is located at{' '}
          <code>/src/components/Badge.js</code>
        </SectionDescription>
      </Section>

      <Section>
        <SectionTitle>Badge Variants</SectionTitle>
        <SectionDescription>
          Different color variants for various use cases and semantic meanings.
        </SectionDescription>

        <ComponentDemo label="Color Variants" gap="12px" wrap>
          <Badge variant="default">Default</Badge>
          <Badge variant="success">Success</Badge>
          <Badge variant="primary">Primary</Badge>
          <Badge variant="info">Info</Badge>
          <Badge variant="warning">Warning</Badge>
          <Badge variant="error">Error</Badge>
          <Badge variant="alert">Alert</Badge>
          <Badge variant="premium">Premium</Badge>
          <Badge variant="new">New</Badge>
          <Badge variant="accent">Accent</Badge>
          <Badge variant="neutral">Neutral</Badge>
          <Badge variant="location">Location</Badge>
          <Badge variant="dark">Dark</Badge>
        </ComponentDemo>

        <CodeBlock>
{`import Badge from 'components/Badge';

<Badge variant="success">Success</Badge>
<Badge variant="primary">Primary</Badge>
<Badge variant="warning">Warning</Badge>
<Badge variant="error">Error</Badge>
<Badge variant="premium">Premium</Badge>
<Badge variant="new">New</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Badge Shapes</SectionTitle>
        <SectionDescription>
          Three shape options: rounded (default), pill (fully rounded), and square.
        </SectionDescription>

        <ComponentDemo label="Shapes" gap="12px" wrap>
          <Badge shape="rounded" variant="success">
            Rounded
          </Badge>
          <Badge shape="pill" variant="primary">
            Pill Shape
          </Badge>
          <Badge shape="square" variant="warning">
            Square
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`// Default rounded corners
<Badge variant="success">Rounded</Badge>

// Pill shape (fully rounded)
<Badge shape="pill" variant="primary">Pill Shape</Badge>

// Square corners
<Badge shape="square" variant="warning">Square</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Badge Sizes</SectionTitle>
        <SectionDescription>
          Three size options: small, medium (default), and large.
        </SectionDescription>

        <ComponentDemo label="Sizes" gap="12px" wrap align="center">
          <Badge size="small" variant="success">
            Small
          </Badge>
          <Badge size="medium" variant="primary">
            Medium
          </Badge>
          <Badge size="large" variant="warning">
            Large
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge size="small">Small</Badge>
<Badge size="medium">Medium</Badge>
<Badge size="large">Large</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Badges with Counts</SectionTitle>
        <SectionDescription>
          Add a count badge to show numbers or short text. Commonly used for pattern
          badges showing frequency or occurrences.
        </SectionDescription>

        <ComponentDemo label="Count Badges" gap="12px" wrap>
          <Badge shape="pill" count={5}>
            Purchase Pattern
          </Badge>
          <Badge shape="pill" count={12}>
            Event Preference
          </Badge>
          <Badge shape="pill" count="3x">
            Division
          </Badge>
          <Badge shape="pill" variant="success" count={99}>
            Popular
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge shape="pill" count={5}>Purchase Pattern</Badge>
<Badge shape="pill" count={12}>Event Preference</Badge>
<Badge shape="pill" count="3x">Division</Badge>
<Badge shape="pill" variant="success" count={99}>Popular</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Badges with Icons</SectionTitle>
        <SectionDescription>
          Add icons to badges for visual clarity and better communication. Icons are
          automatically sized and aligned based on the badge size.
        </SectionDescription>

        <ComponentDemo label="Icon Badges" gap="12px" wrap align="center">
          <Badge
            variant="accent"
            uppercase
            icon={
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
              </svg>
            }
          >
            Scanned
          </Badge>
          <Badge
            variant="success"
            icon={
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path
                  fillRule="evenodd"
                  d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
                />
              </svg>
            }
          >
            Info
          </Badge>
          <Badge
            variant="warning"
            size="small"
            icon={
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
              </svg>
            }
          >
            Alert
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge
  variant="accent"
  uppercase
  icon={
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="..." />
    </svg>
  }
>
  Scanned
</Badge>

<Badge
  variant="success"
  icon={<svg>...</svg>}
>
  Info
</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Outline Badges</SectionTitle>
        <SectionDescription>
          Outline style with border and transparent background. Great for promo codes and
          featured content.
        </SectionDescription>

        <ComponentDemo label="Outline Style" gap="12px" wrap>
          <Badge outline>SUMMER2024</Badge>
          <Badge outline uppercase>
            PROMO
          </Badge>
          <Badge outline size="large">
            Featured
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge outline>SUMMER2024</Badge>
<Badge outline uppercase>PROMO</Badge>
<Badge outline size="large">Featured</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Custom Color Badges</SectionTitle>
        <SectionDescription>
          Use the <code>color</code> prop to specify custom colors for brand-specific or
          unique badges.
        </SectionDescription>

        <ComponentDemo label="Custom Colors" gap="12px" wrap>
          <Badge color="#8B5CF6">Custom Purple</Badge>
          <Badge color="#EC4899">Custom Pink</Badge>
          <Badge color="#10B981">Custom Green</Badge>
          <Badge color="#F59E0B">Custom Orange</Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge color="#8B5CF6">Custom Purple</Badge>
<Badge color="#EC4899">Custom Pink</Badge>
<Badge color="#10B981">Custom Green</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Interactive Badges</SectionTitle>
        <SectionDescription>
          Interactive badges show hover effects and can be clicked. Use for filter pills or
          clickable tags.
        </SectionDescription>

        <ComponentDemo label="Interactive Badges" gap="12px" wrap>
          <Badge shape="pill" interactive onClick={() => alert('Clicked!')}>
            Clickable Tag
          </Badge>
          <Badge shape="pill" interactive variant="primary">
            Filter Option
          </Badge>
          <Badge shape="pill" interactive count={5}>
            Category
          </Badge>
        </ComponentDemo>

        <CodeBlock>
{`<Badge
  shape="pill"
  interactive
  onClick={() => handleClick()}
>
  Clickable Tag
</Badge>

<Badge shape="pill" interactive variant="primary">
  Filter Option
</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Real-World Examples</SectionTitle>
        <SectionDescription>
          Common use cases from the application.
        </SectionDescription>

        <Card title="Customer Segments" headerBorder>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px 0' }}>
            <Badge color="#22C55E">Super Fan</Badge>
            <Badge color="#3B82F6">Regular Fan</Badge>
            <Badge color="#06B6D4">Casual Fan</Badge>
            <Badge color="#F59E0B">First-Timer</Badge>
            <Badge color="#DC2626">Inactive</Badge>
          </div>
        </Card>

        <Card
          title="Purchase Patterns"
          headerBorder
          style={{ marginTop: '16px' }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px 0' }}>
            <Badge shape="pill" count={8}>
              Weekend Buyer
            </Badge>
            <Badge shape="pill" count={5}>
              Early Bird
            </Badge>
            <Badge shape="pill" count={12}>
              Group Purchaser
            </Badge>
          </div>
        </Card>

        <Card
          title="Transaction Details"
          headerBorder
          style={{ marginTop: '16px' }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px 0' }}>
            <Badge uppercase>CREDIT CARD</Badge>
            <Badge uppercase>ONLINE</Badge>
            <Badge outline uppercase>
              SUMMER2024
            </Badge>
          </div>
        </Card>

        <Card
          title="Ticket Status"
          headerBorder
          style={{ marginTop: '16px' }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '8px 0' }}>
            <Badge
              variant="accent"
              uppercase
              icon={
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                </svg>
              }
            >
              Credited
            </Badge>
            <Badge
              variant="accent"
              uppercase
              icon={
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                </svg>
              }
            >
              Scanned
            </Badge>
            <Badge variant="dark" shape="square" size="small" uppercase>
              A123
            </Badge>
          </div>
        </Card>

        <CodeBlock>
{`// Customer segments
<Badge color="#22C55E">Super Fan</Badge>
<Badge color="#3B82F6">Regular Fan</Badge>

// Purchase patterns with counts
<Badge shape="pill" count={8}>Weekend Buyer</Badge>
<Badge shape="pill" count={5}>Early Bird</Badge>

// Transaction details
<Badge uppercase>CREDIT CARD</Badge>
<Badge outline uppercase>SUMMER2024</Badge>

// Ticket status with icons
<Badge variant="accent" uppercase icon={<svg>...</svg>}>
  CREDITED
</Badge>
<Badge variant="accent" uppercase icon={<svg>...</svg>}>
  SCANNED
</Badge>
<Badge variant="dark" shape="square" size="small" uppercase>
  A123
</Badge>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="Badge Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>children</strong> (node, required): Badge content
            <br />
            • <strong>variant</strong> (string): Color variant - "default", "success",
            "primary", "info", "warning", "error", "alert", "premium", "new", "accent",
            "neutral", "location", "dark"
            <br />
            • <strong>shape</strong> (string): Shape - "rounded" (default), "pill",
            "square"
            <br />
            • <strong>size</strong> (string): Size - "small", "medium" (default), "large"
            <br />
            • <strong>color</strong> (string): Custom color (hex or rgb)
            <br />
            • <strong>count</strong> (number | string): Optional count badge
            <br />
            • <strong>icon</strong> (node): Optional icon element (automatically sized and
            aligned)
            <br />
            • <strong>outline</strong> (boolean): Outline style - default: false
            <br />
            • <strong>uppercase</strong> (boolean): Uppercase text - default: false
            <br />
            • <strong>interactive</strong> (boolean): Show hover effects - default: false
            <br />
            • <strong>onClick</strong> (function): Click handler
            <br />
            • <strong>className</strong> (string): Additional CSS class
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Use semantic variants (success, warning, error) for status indicators
              <br />
              • Use pill shape for tags and pattern badges
              <br />
              • Add counts to show frequency or occurrences
              <br />
              • Use interactive prop for clickable/filterable badges
              <br />
              • Keep badge text short and concise
              <br />• Use custom colors for brand-specific categories (segments)
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't use too many different colors in one view
              <br />
              • Don't make non-interactive badges look clickable
              <br />
              • Don't put long text in badges (use labels or tags instead)
              <br />
              • Don't forget to use uppercase for codes and identifiers
              <br />• Don't overuse outline badges (they should stand out)
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Use Cases</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Status Indicators (with icons):</strong>
            <br />• Transaction status (credited, refunded, scanned)
            <br />• Order status and payment status
            <br />• User roles and permissions
            <br />• Feature flags and settings
            <br />
            <br />
            <strong>Categories and Tags:</strong>
            <br />• Customer segments with custom colors
            <br />• Product categories and types
            <br />• Event types and divisions
            <br />
            <br />
            <strong>Pattern Badges:</strong>
            <br />• Purchase patterns with counts
            <br />• Timing patterns and preferences
            <br />• Behavioral insights
            <br />
            <br />
            <strong>Labels and Identifiers:</strong>
            <br />• Promo codes (outline style, uppercase)
            <br />• Serial number suffixes
            <br />• Location categories
            <br />• Payment methods and order sources (uppercase)
          </div>
        </Card>
      </Section>
    </div>
  );
};

export default BadgesDocs;